import { Injectable } from '@angular/core';
import {SharedService} from "../../shared/shared.service";
import {TenantService} from "../../tenant/tenant.service";
import {WarehouseDataService} from "./warehouse.data.service";
import { catchError, Observable, tap } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { JsonService } from 'src/app/services/core/json.service';
import { WarehouseApiResources } from './warehouse.constants';
import {throwError as observableThrowError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class WarehouseService {

  public groups: any;
  public ratios: any;
  public staffs: any;
  private baseURL:any;

  constructor(
    public sharedService: SharedService,
    public tenantService: TenantService,
    public warehouseDataService: WarehouseDataService,
    private http: HttpClient,
    private jsonService: JsonService,
  ) {
    this.baseURL = WarehouseApiResources;
  }


  getWarehouses = () => {
   return this.warehouseDataService.list();
  }

  list(model: any = null): Observable<any> {
    const endPoint = `${this.baseURL.search}`;
    return this.http.post<any>(endPoint,model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // ==========================================
  // Zone Management Methods
  // ==========================================

  /**
   * Get zone templates
   */
  getZoneTemplates = () => {
    return this.warehouseDataService.getZoneTemplates();
  }

  /**
   * Get all zones for a warehouse
   */
  getZones = (warehouseId: any) => {
    return this.warehouseDataService.getZonesByWarehouse(warehouseId);
  }

  /**
   * Get zones with visual layout (FLOW 2)
   */
  getZonesVisualLayout = (warehouseId: any) => {
    return this.warehouseDataService.getZonesVisualLayout(warehouseId);
  }

  /**
   * Create new zone
   */
  createZone = (warehouseId: any, zoneData: any) => {
    return this.warehouseDataService.createZone(warehouseId, zoneData);
  }

  /**
   * Get zone details
   */
  getZone = (id: any) => {
    return this.warehouseDataService.getZone(id);
  }

  /**
   * Update zone
   */
  updateZone = (id: any, updates: any) => {
    return this.warehouseDataService.updateZone(id, updates);
  }

  /**
   * Delete zone
   */
  deleteZone = (id: any) => {
    return this.warehouseDataService.deleteZone(id);
  }

  /**
   * Get slot details
   */
  getSlotDetails = (zoneId: any, slotId: any) => {
    return this.warehouseDataService.getSlotDetails(zoneId, slotId);
  }

  // ==========================================
  // Floor Management Methods
  // ==========================================

  /**
   * Get all floors for a warehouse
   */
  getFloors = (warehouseId: any) => {
    return this.warehouseDataService.getFloorsByWarehouse(warehouseId);
  }

  /**
   * Search floors (autocomplete)
   */
  searchFloors = (query: string, warehouseId: any) => {
    return this.warehouseDataService.searchFloors(query, warehouseId);
  }

  /**
   * Find or create floor
   */
  findOrCreateFloor = (floorName: string, warehouseId: any) => {
    return this.warehouseDataService.findOrCreateFloor(floorName, warehouseId);
  }

  // ==========================================
  // Inventory Management Methods
  // ==========================================

  /**
   * Add material to inventory (FLOW 4)
   */
  addInventory = (inventoryData: any) => {
    return this.warehouseDataService.addInventory(inventoryData);
  }

  /**
   * Update an inventory item's master data (Edit Item — row-actions
   * Action 4). Master-data only; quantity/location go through their
   * own flows.
   */
  updateInventoryItem = (id: number, payload: any) => {
    return this.warehouseDataService.updateInventoryItem(id, payload);
  }

  /**
   * Get inventory items
   */
  getInventoryItems = (params: any = {}) => {
    return this.warehouseDataService.getInventoryItems(params);
  }

  /**
   * Search products for inventory
   */
  searchProducts = (searchParams: any = {}) => {
    return this.warehouseDataService.searchProducts(searchParams);
  }

  /**
   * Get all products
   */
  getProducts = (searchParams: any = {}) => {
    return this.warehouseDataService.getProducts(searchParams);
  }

  /**
   * Get all locations for a material (FLOW 8: Multi-Location Support)
   */
  getMaterialLocations = (materialId: number, warehouseId: number) => {
    return this.warehouseDataService.getMaterialLocations(materialId, warehouseId);
  }

  /**
   * Adjust inventory stock (FLOW 9: Adjust Inventory Quantities)
   */
  adjustInventory = (inventoryId: number, adjustmentData: any) => {
    return this.warehouseDataService.adjustInventory(inventoryId, adjustmentData);
  }

  /**
   * Get items in a bin location (FLOW 11: Cycle Counting)
   */
  getBinItems = (binCode: string, warehouseId: number) => {
    return this.warehouseDataService.getBinItems(binCode, warehouseId);
  }

  /**
   * Process cycle count result (FLOW 11: Cycle Counting)
   */
  processCycleCount = (countData: any) => {
    return this.warehouseDataService.processCycleCount(countData);
  }

  /**
   * Get reservations for inventory item (FLOW 12: Reservations Management)
   */
  getInventoryReservations = (inventoryId: number) => {
    return this.warehouseDataService.getInventoryReservations(inventoryId);
  }

  /**
   * Get batch records for material (FLOW 13: Batch/Lot Tracking)
   */
  getMaterialBatches = (materialId: number, warehouseId: number) => {
    return this.warehouseDataService.getMaterialBatches(materialId, warehouseId);
  }

  /**
   * Batch Management Methods (FLOW 14)
   */
  adjustBatchQuantity = (batchId: string, adjustmentQuantity: number, reason: string) => {
    return this.warehouseDataService.adjustBatchQuantity(batchId, adjustmentQuantity, reason);
  }

  splitBatch = (batchId: string, splits: Array<{quantity: number, batch_number: string}>) => {
    return this.warehouseDataService.splitBatch(batchId, splits);
  }

  transferBatch = (batchId: string, toZoneId: number, toBinCode: string, quantity?: number) => {
    return this.warehouseDataService.transferBatch(batchId, toZoneId, toBinCode, quantity);
  }

  getBatchDetails = (batchId: string) => {
    return this.warehouseDataService.getBatchDetails(batchId);
  }

  /**
   * Reservation Management Methods (FLOW 15)
   */
  releaseReservation = (reservationId: string, reason?: string) => {
    return this.warehouseDataService.releaseReservation(reservationId, reason);
  }

  extendReservation = (reservationId: string, newDate: string) => {
    return this.warehouseDataService.extendReservation(reservationId, newDate);
  }

  transferReservation = (reservationId: string, toJobId?: number, toPickupId?: number) => {
    return this.warehouseDataService.transferReservation(reservationId, toJobId, toPickupId);
  }

  fulfillReservation = (reservationId: string, fulfilledQuantity: number, notes?: string) => {
    return this.warehouseDataService.fulfillReservation(reservationId, fulfilledQuantity, notes);
  }

  createReservation = (data: any) => {
    return this.warehouseDataService.createReservation(data);
  }

  /**
   * QR Code Methods (FLOW 16)
   */
  generateQRCode = (inventoryId: number, options: any = {}) => {
    return this.warehouseDataService.generateQRCode(inventoryId, options);
  }

  getQRData = (inventoryId: number) => {
    return this.warehouseDataService.getQRData(inventoryId);
  }

  /**
   * Summary Metrics (FLOW 23 — warehouse-wide aggregates)
   */
  getInventoryMetrics = (warehouseId: number) => {
    return this.warehouseDataService.getInventoryMetrics(warehouseId);
  }

  /**
   * Slot Analysis (FLOW 22 — zone distribution + storage metrics)
   */
  getSlotAnalysis = (inventoryId: number) => {
    return this.warehouseDataService.getSlotAnalysis(inventoryId);
  }

  /**
   * Document Methods (FLOW 21 — polymorphic Document system, same as Bid/Proposal)
   */
  getInventoryDocuments = (inventoryId: number) => {
    return this.warehouseDataService.getInventoryDocuments(inventoryId);
  }

  uploadInventoryDocument = (inventoryId: number, file: File) => {
    return this.warehouseDataService.uploadInventoryDocument(inventoryId, file);
  }

  deleteInventoryDocument = (inventoryId: number, documentId: number) => {
    return this.warehouseDataService.deleteInventoryDocument(inventoryId, documentId);
  }

  getInventoryDocumentDownloadUrl = (inventoryId: number, documentId: number) => {
    return this.warehouseDataService.getInventoryDocumentDownloadUrl(inventoryId, documentId);
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }


}
